import React, { useState, useEffect } from "react";
import axios from "axios";
import { Filter, ShoppingBag, RotateCcw, Sparkles, X, ChevronRight } from "lucide-react";
import ThreeCanvas from "./ThreeCanvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ["Todos", "Streetwear", "Luxury", "Techwear"];

export default function ProductCatalog({ onAddToCart }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [activeProduct, setActiveProduct] = useState(null); // Detailed view modal
  
  // Custom states for detail modal
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (err) {
        console.error("Error fetching products", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = selectedCategory === "Todos"
    ? products
    : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

  const handleOpenDetail = (product) => {
    setActiveProduct(product);
    setSelectedSize(product.sizes[0] || "");
    setSelectedColor(product.colors[0] || "");
  };

  const handleAddToCartSubmit = () => {
    if (!activeProduct) return;
    
    onAddToCart({
      productId: activeProduct._id || activeProduct.id,
      name: activeProduct.name,
      price: activeProduct.price,
      imageUrl: activeProduct.imageUrl,
      size: selectedSize,
      color: selectedColor,
      quantity: 1
    });

    // Close detail modal on add to cart
    setActiveProduct(null);
  };

  const getColorHex = (colorName) => {
    const mapping = {
      "Carbon Black": "#1A1A1A",
      "Neon Cobalt": "#3B82F6",
      "Alabaster White": "#F3F4F6",
      "Slate Grey": "#6B7280",
      "Stealth Black": "#0F172A",
      "Safety Orange": "#F97316",
      "Emerald Green": "#10B981",
      "Void Black": "#030712",
      "Alpha Khaki": "#78350F"
    };
    return mapping[colorName] || "#4F46E5";
  };

  return (
    <section id="shop" className="py-24 bg-white text-text_primary font-body">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-widest text-[#FF3B30] block mb-2">
              WOVLL STORE // PIEZAS PREMIUM
            </span>
            <h2 className="font-heading italic text-5xl md:text-7xl font-semibold tracking-tighter leading-tight">
              Catálogo de Productos
            </h2>
          </div>

          {/* Categories filter list */}
          <div className="flex flex-wrap gap-2 border-b border-border_subtle pb-4">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`product-filter-${cat.toLowerCase()}`}
                className={`px-4 py-2 font-mono-accent text-xs uppercase tracking-wider transition-all duration-150 ${
                  selectedCategory === cat
                    ? "bg-[#0A0A0A] text-white"
                    : "bg-transparent text-text_secondary hover:text-text_primary"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="h-64 flex items-center justify-center font-mono-accent text-xs uppercase tracking-widest text-text_secondary">
            Cargando Colección Wovll...
          </div>
        ) : (
          /* Products Tetris Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((prod) => (
              <div
                key={prod._id || prod.id}
                data-testid={`product-card-${prod._id || prod.id}`}
                className="group border border-border_subtle flex flex-col justify-between p-4 cursor-pointer hover:border-text_primary transition-all rounded-none"
                onClick={() => handleOpenDetail(prod)}
              >
                {/* Image Container with high contrast scale-down on hover */}
                <div className="aspect-[3/4] w-full overflow-hidden bg-[#F4F4F2] relative">
                  <img
                    src={prod.imageUrl}
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-95 transition-transform duration-700 ease-out"
                  />
                  {prod.isFeatured && (
                    <div className="absolute top-4 left-4 bg-text_primary text-white text-[9px] font-mono-accent uppercase tracking-widest px-2.5 py-1" style={{ backgroundColor: "#0A0A0A" }}>
                      Featured
                    </div>
                  )}
                  {/* Subtle hover prompt */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300">
                    <span className="bg-white text-black font-mono-accent text-xs uppercase tracking-widest px-4 py-2.5 rounded-none shadow-lg">
                      Ver Visor 3D
                    </span>
                  </div>
                </div>

                {/* Details info */}
                <div className="mt-4 flex justify-between items-start pt-4 border-t border-border_subtle">
                  <div>
                    <span className="font-mono-accent text-[9px] uppercase tracking-widest text-text_secondary block mb-1">
                      {prod.category}
                    </span>
                    <h3 className="font-heading italic text-xl font-semibold group-hover:underline">
                      {prod.name}
                    </h3>
                  </div>
                  <span className="font-mono-accent font-semibold text-sm">
                    {prod.price.toFixed(2)} €
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interactive 3D Product Viewer Detail Modal */}
      {activeProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
            onClick={() => setActiveProduct(null)}
          />

          {/* Modal Container */}
          <div
            data-testid="product-detail-modal"
            className="bg-[#F4F4F2] text-text_primary max-w-5xl w-full grid grid-cols-1 md:grid-cols-12 shadow-2xl relative z-10 border border-border_subtle rounded-none overflow-hidden"
          >
            {/* Close button */}
            <button
              onClick={() => setActiveProduct(null)}
              data-testid="modal-close-button"
              className="absolute top-4 right-4 z-20 text-text_primary hover:text-[#FF3B30] transition-colors p-2 bg-white/80 backdrop-blur border border-border_subtle"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Left Column: Interactive 3D product viewer Canvas */}
            <div className="md:col-span-6 h-[320px] md:h-auto min-h-[400px] bg-white border-r border-border_subtle relative flex flex-col">
              <div className="absolute top-4 left-4 z-10">
                <span className="font-mono-accent text-[10px] uppercase tracking-widest text-[#4F46E5] flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                  VISOR INTERACTIVO 3D
                </span>
                <span className="text-[9px] font-light text-text_secondary block mt-0.5">
                  Haz click y arrastra para rotar la prenda en 3D
                </span>
              </div>

              {/* R3F Canvas showing the shape with current selected color */}
              <div className="flex-1">
                <ThreeCanvas
                  shapeType={activeProduct.shapeType}
                  color={getColorHex(selectedColor)}
                  isInteractive={true}
                />
              </div>

              {/* Texture detail box in corner */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#F4F4F2]/90 backdrop-blur p-3 border border-border_subtle flex items-center justify-between">
                <div className="font-mono-accent text-[9px] uppercase tracking-widest text-text_secondary">
                  <span>Geometría: {activeProduct.shapeType}</span>
                </div>
                <button 
                  onClick={() => {
                    setSelectedColor(activeProduct.colors[0]);
                    setSelectedSize(activeProduct.sizes[0]);
                  }}
                  className="text-text_secondary hover:text-text_primary transition-colors flex items-center gap-1 font-mono-accent text-[9px] uppercase tracking-widest"
                >
                  <RotateCcw className="w-3 h-3" />
                  Resetear
                </button>
              </div>
            </div>

            {/* Right Column: Clothing Description, Sizing and Colors Selection */}
            <div className="md:col-span-6 p-8 md:p-12 flex flex-col justify-between bg-[#F4F4F2]">
              <div>
                <span className="font-mono-accent text-xs uppercase tracking-widest text-text_secondary block mb-2">
                  WOVLL COLL. 2026 // {activeProduct.category}
                </span>
                <h2 className="font-heading italic text-4xl md:text-5xl font-semibold leading-tight mb-4">
                  {activeProduct.name}
                </h2>
                <div className="font-mono-accent font-semibold text-2xl mb-6">
                  {activeProduct.price.toFixed(2)} €
                </div>

                <p className="text-sm md:text-base text-text_secondary leading-relaxed font-light mb-8">
                  {activeProduct.description}
                </p>

                {/* Color Selector */}
                <div className="mb-6">
                  <h4 className="font-mono-accent text-xs uppercase tracking-widest text-text_primary mb-3">
                    Color: <span className="font-semibold text-text_secondary">{selectedColor}</span>
                  </h4>
                  <div className="flex gap-3">
                    {activeProduct.colors.map((color) => {
                      const isSelected = selectedColor === color;
                      return (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          data-testid={`product-color-select-${color.toLowerCase().replace(" ", "-")}`}
                          className={`w-8 h-8 rounded-none border-2 transition-all flex items-center justify-center ${
                            isSelected ? "border-[#0A0A0A] scale-110" : "border-transparent opacity-60 hover:opacity-100"
                          }`}
                          title={color}
                        >
                          <span
                            className="w-5 h-5 rounded-none border border-black/10 inline-block"
                            style={{ backgroundColor: getColorHex(color) }}
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Size Selector */}
                <div className="mb-8">
                  <h4 className="font-mono-accent text-xs uppercase tracking-widest text-text_primary mb-3">
                    Talla Seleccionada: <span className="font-semibold text-text_secondary">{selectedSize}</span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {activeProduct.sizes.map((size) => {
                      const isSelected = selectedSize === size;
                      return (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          data-testid={`product-size-select-${size.toLowerCase()}`}
                          className={`px-4 py-2 border font-mono-accent text-xs uppercase tracking-wider transition-colors ${
                            isSelected
                              ? "bg-[#0A0A0A] text-white border-transparent"
                              : "bg-white text-text_secondary border-border_subtle hover:text-text_primary hover:border-text_primary"
                          }`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Action purchase Buttons */}
              <div className="pt-6 border-t border-border_subtle space-y-4">
                <button
                  onClick={handleAddToCartSubmit}
                  data-testid="add-to-cart-button"
                  className="w-full py-4 bg-[#0A0A0A] text-white hover:bg-[#FF3B30] transition-colors font-mono-accent uppercase tracking-widest text-xs flex items-center justify-center gap-3"
                >
                  <ShoppingBag className="w-4 h-4" />
                  Añadir a la Bolsa
                </button>
                <div className="flex justify-between font-mono-accent text-[9px] text-text_secondary uppercase tracking-widest px-1">
                  <span>Stock disponible: {activeProduct.stock} uds</span>
                  <span className="text-[#4F46E5] font-semibold">Envío Express Gratis</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
